import { useState, useEffect, useContext } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
    Zap, Copy, Eye, EyeOff, RefreshCw, CheckCircle, AlertTriangle, Loader2,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { WEBHOOK_CONFIG_URL, WEBHOOK_ROTATE_URL, API_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { AuthContext } from "../../context/AuthContext";

// ── tiny copy helper ──────────────────────────────────────────────────────────

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <button
            onClick={handleCopy}
            title="Copy to clipboard"
            className="ml-2 p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
            {copied
                ? <CheckCircle className="h-4 w-4 text-green-500" />
                : <Copy className="h-4 w-4" />}
        </button>
    );
};

// ── read-only field with copy ─────────────────────────────────────────────────

const CopyField = ({ label, value, masked, hint }) => {
    const [show, setShow] = useState(false);
    const display = masked && !show
        ? value.slice(0, 4) + '••••••••••••••••••••••••' + value.slice(-4)
        : value;

    return (
        <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {label}
            </label>
            <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
                <span className="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200 break-all select-all">
                    {display}
                </span>
                {masked && (
                    <button
                        onClick={() => setShow(s => !s)}
                        className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        title={show ? 'Hide' : 'Reveal'}
                    >
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                )}
                <CopyButton text={value} />
            </div>
            {hint && <p className="text-[10px] text-gray-400 dark:text-gray-500">{hint}</p>}
        </div>
    );
};

// ── main component ────────────────────────────────────────────────────────────

export default function ZapierWebhook() {
    const { fetch, create } = useApiMethods();
    const { isEngageTenant } = useContext(AuthContext);
    const isEngage = isEngageTenant();

    const [config, setConfig]           = useState(null);
    const [loading, setLoading]         = useState(true);
    const [rotating, setRotating]       = useState(false);
    const [confirmRotate, setConfirmRotate] = useState(false);

    const [notAvailable, setNotAvailable] = useState(false);

    const loadConfig = () => {
        setLoading(true);
        fetch(WEBHOOK_CONFIG_URL)
            .then(res => {
                if (res?.status === 'success') {
                    setConfig(res.data);
                } else if (res?.message === 'Tenant not found') {
                    setNotAvailable(true);
                } else {
                    ToastMsg('error', res?.message || 'Failed to load webhook config');
                }
            })
            .catch(() => ToastMsg('error', 'Failed to load webhook config'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadConfig(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRotate = () => {
        setRotating(true);
        setConfirmRotate(false);
        create(WEBHOOK_ROTATE_URL, {})
            .then(res => {
                if (res?.status === 'success') {
                    setConfig(res.data);
                    ToastMsg('success', 'Secret rotated — update your Zapier webhook now');
                } else {
                    ToastMsg('error', res?.message || 'Failed to rotate secret');
                }
            })
            .catch(() => ToastMsg('error', 'Failed to rotate secret'))
            .finally(() => setRotating(false));
    };

    const fullWebhookUrl = config
        ? `${API_URL.replace(/\/$/, '')}${config.webhook_url}`
        : '';

    return (
        <ContainerCard>
            {/* header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                    <Zap className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Zapier / Webhook Integration
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Use this URL and secret key to receive leads from Zapier, Meta, or any external source.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-8 justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading…
                </div>
            ) : notAvailable ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                    <Zap className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Webhook integration is not available for this account.
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        This feature requires an organization account. Contact your administrator.
                    </p>
                </div>
            ) : config ? (
                <div className="space-y-5">

                    {/* Webhook URL */}
                    <CopyField
                        label="Webhook URL"
                        value={fullWebhookUrl}
                        hint="Use this as the webhook endpoint in Zapier or any HTTP integration."
                    />

                    {/* Secret Key */}
                    <CopyField
                        label="Secret Key"
                        value={config.webhook_secret}
                        masked
                        hint='Send this as the "X-Webhook-Key" header in every request.'
                    />

                    {/* Projects */}
                    {!isEngage && config.projects && config.projects.length > 0 && (
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Project Key (project_id)
                            </label>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                Pass one of these IDs as <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">project_id</code> in the Zapier payload to assign the lead to a project.
                            </p>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-100 dark:bg-gray-800">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 w-20">ID</th>
                                            <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Project Name</th>
                                            <th className="px-3 py-2 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {config.projects.map(p => (
                                            <tr key={p.id} className="bg-white dark:bg-gray-900">
                                                <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300">{p.id}</td>
                                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{p.name}</td>
                                                <td className="px-3 py-2">
                                                    <CopyButton text={String(p.id)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* How to set up in Zapier */}
                    <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-2">
                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                            Zapier Setup (Webhooks by Zapier)
                        </p>
                        <ol className="text-xs text-gray-500 dark:text-gray-400 space-y-1 list-decimal pl-4">
                            <li>Add a <strong>Webhooks by Zapier</strong> action → <em>POST</em></li>
                            <li>Set URL to the <strong>Webhook URL</strong> above</li>
                            <li>Add a header: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">X-Webhook-Key</code> = <strong>Secret Key</strong> above</li>
                            <li>Map fields: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">phone_number</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">name</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">email</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">city</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">form_name</code></li>
                            <li>For the ad reports, also map from the Lead Ads trigger: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">lead_id</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">campaign_id</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">campaign_name</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">adset_id</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ad_id</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ad_name</code></li>
                            {/* Hospital tenants file leads as enquiries, which carry no project. */}
                            {!isEngage && (
                                <li>Optionally add <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">project_id</code> using an ID from the <strong>Project Key</strong> table above</li>
                            )}
                        </ol>
                    </div>

                    {/* Rotate section */}
                    <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                        {confirmRotate ? (
                            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 space-y-3">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-red-700 dark:text-red-300">
                                        <strong>This will immediately invalidate your current secret.</strong><br />
                                        All Zapier zaps using the old key will stop working until you update them.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={handleRotate}
                                        disabled={rotating}
                                    >
                                        {rotating
                                            ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Rotating…</>
                                            : 'Yes, rotate secret'
                                        }
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setConfirmRotate(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Rotate Secret</p>
                                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                        Generate a new secret if the current one is compromised.
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setConfirmRotate(true)}
                                >
                                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                                    Rotate Secret
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                    Webhook configuration unavailable.
                </p>
            )}
        </ContainerCard>
    );
}
